import type { FormApproval } from '../../types';
import { SectionCard } from './SectionCard';

/**
 * Workflow & approvals — vertical timeline of steps lifted from the KB
 * article's structured JSON. Steps are split into the Core Model flow and
 * country-specific exceptions (criteria like "Only when the country of
 * Requested for is Turkiye"). The split is computed server-side via a
 * regex on the criteria text — see `_classify_approval_scope`.
 */
export function ApprovalsTimeline({ steps }: { steps: FormApproval[] }) {
  const all = steps ?? [];
  const core = all.filter((s) => s.scope !== 'country');
  const country = all.filter((s) => s.scope === 'country');

  return (
    <SectionCard
      title="Workflow & Approvals"
      count={all.length}
      defaultOpen
      emptyHint="No workflow steps documented for this form."
    >
      {all.length > 0 && (
        <div className="space-y-5">
          <ApprovalsGroup
            label="Core Model"
            tone="brand"
            steps={core}
            emptyHint="No Core Model steps documented."
          />
          <ApprovalsGroup
            label="Country exceptions"
            tone="amber"
            steps={country}
            emptyHint="No country-specific exceptions detected."
          />
        </div>
      )}
    </SectionCard>
  );
}

function ApprovalsGroup({
  label,
  tone,
  steps,
  emptyHint,
}: {
  label: string;
  tone: 'brand' | 'amber';
  steps: FormApproval[];
  emptyHint: string;
}) {
  const headerCls =
    tone === 'amber'
      ? 'text-amber-800 bg-amber-50 border-amber-200'
      : 'text-brand-danone bg-brand-50 border-brand-100';
  const dotCls =
    tone === 'amber' ? 'bg-amber-500' : 'bg-brand-danone';
  const railCls =
    tone === 'amber' ? 'border-amber-200' : 'border-slate-200';

  return (
    <section>
      <header
        className={`inline-flex items-center gap-2 text-[11px] uppercase tracking-wider font-semibold rounded-md border px-2 py-0.5 mb-3 ${headerCls}`}
      >
        <span>{label}</span>
        <span className="text-[10px] opacity-70">({steps.length})</span>
      </header>
      {steps.length === 0 ? (
        <p className="text-sm text-slate-400 italic">{emptyHint}</p>
      ) : (
        <ol className={`relative border-l-2 ${railCls} ml-1.5 space-y-4`}>
          {steps.map((s, i) => (
            <li
              key={`${s.order ?? i}-${s.type ?? ''}-${i}`}
              className="pl-4 -ml-px"
            >
              <span
                aria-hidden
                className={`absolute -left-[7px] mt-1.5 h-3 w-3 rounded-full ring-2 ring-white ${dotCls}`}
              />
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                <span className="text-xs font-mono text-slate-400">
                  {s.order ?? i + 1}
                </span>
                {s.type && (
                  <span className="text-sm font-semibold text-slate-900">
                    {s.type}
                  </span>
                )}
                {s.assignment_group && (
                  <span className="text-xs text-slate-600">
                    → {s.assignment_group}
                  </span>
                )}
              </div>
              {s.description && (
                <p className="mt-1 text-sm text-slate-700 whitespace-pre-line">
                  {s.description}
                </p>
              )}
              {s.criteria && (
                <p className="mt-1 text-xs text-slate-500">
                  <span className="uppercase tracking-wide">Criteria:</span>{' '}
                  {s.criteria}
                </p>
              )}
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
